import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFocusEffect } from '@react-navigation/native'
import moment from 'moment'
import { DateData } from 'react-native-calendars'
import { userSelector } from '@src/selectors/app'
import Storage from '@src/utils/storage'
import {
  Dates,
  EhrAppointmentKind,
  EhrAppointmentProviderKind,
  EhrAppointmentRecurrenceCadence,
  EhrSlot,
  Slots,
} from '@src/types'
import Analytics, { MixpanelEventProperties, CustomEventTypes } from '@src/config/analytics'
import { loadingEffectsSelector } from '@src/selectors/loading'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import {
  insurancePolicySelector,
  latestUserEhrAppointmentSelector,
} from './models/nutritionistHub.selectors'
import { isUnusedCallsInfoAvailable } from './utils'
import { useAppointment } from './components/VideoCallModal/useAppointment'

const MONTH_FORMAT = 'YYYY-MM'

export const useEhrAvailableDates = (
  appointmentKind: EhrAppointmentKind,
  cadence: EhrAppointmentRecurrenceCadence,
  appointmentId?: string,
  coachId?: string,
  switchingCoach?: boolean,
  initialDate?: string,
) => {
  const dispatch = useDispatch()
  const appointmentSchedulerMonthlyDateFetchingEnabled = useFeatureFlag(
    Feature.AppointmentSchedulerMonthlyDateFetching,
  )

  const {
    appointment: appointmentToReschedule,
    loading: loadingAppointmentToReschedule,
  } = useAppointment({
    id: appointmentId,
    skip: !appointmentSchedulerMonthlyDateFetchingEnabled || !appointmentId,
  })

  const user = useSelector(userSelector)

  const [allAvailableDates, setAllAvailableDates] = useState<string[]>([])
  const [crossKindRecurrenceDates, setCrossKindRecurrenceDates] = useState<string[]>([])
  const [fetchedMonths, setFetchedMonths] = useState<Set<string>>(new Set())
  const [currentAvailableDates, setCurrentAvailableDates] = useState<string[]>([])
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<string | null>(
    initialDate ? moment(initialDate).format(MONTH_FORMAT) : moment().format(MONTH_FORMAT),
  )
  const [error, setError] = useState(false)

  // Track previous reschedule date to update selectedMonth when appointment data arrives
  const [prevRescheduleDate, setPrevRescheduleDate] = useState<string | undefined>(undefined)
  const rescheduleDate = appointmentToReschedule?.meetingAt
  if (rescheduleDate && rescheduleDate !== prevRescheduleDate) {
    setPrevRescheduleDate(rescheduleDate)
    setSelectedMonth(moment(rescheduleDate).format(MONTH_FORMAT))
  }

  const loading =
    useSelector(loadingEffectsSelector)['nutritionistHub/fetchEhrAvailableDays'] ||
    loadingAppointmentToReschedule ||
    false

  const reload = useCallback(
    ({
      startDate,
      endDate,
      appendDates = false,
      month,
    }: {
      startDate?: string
      endDate?: string
      appendDates?: boolean
      month?: string
    } = {}) => {
      const payload = {
        appointmentKind,
        timeZone: user?.timeZone,
        providerKind: EhrAppointmentProviderKind.Coach,
        appointmentId,
        providerId: coachId,
        userState: user?.address?.state,
        switchingCoach,
        startDate: appointmentSchedulerMonthlyDateFetchingEnabled
          ? startDate || moment().toISOString()
          : undefined,
        endDate: appointmentSchedulerMonthlyDateFetchingEnabled
          ? endDate || moment().endOf('month').toISOString()
          : undefined,
        cadence,
      }

      dispatch({
        type: 'nutritionistHub/fetchEhrAvailableDays',
        payload,
        success: (response: Dates) => {
          if (appointmentSchedulerMonthlyDateFetchingEnabled) {
            setAllAvailableDates((prev) =>
              appendDates ? [...prev, ...response.dates] : response.dates,
            )
          }

          setCrossKindRecurrenceDates((prev) => {
            if (!appendDates) {
              return response.crossKindRecurrenceDates
            }

            return Array.from(new Set([...prev, ...response.crossKindRecurrenceDates]))
          })
          setCurrentAvailableDates(response.dates)
          setSelectedDate(response.dates.length > 0 ? response.dates[0] : null)

          if (month) {
            setFetchedMonths((prev) => new Set(prev).add(month))
          }
        },
        failure: () => {
          setError(true)
        },
      })
    },
    [
      appointmentKind,
      cadence,
      dispatch,
      user?.timeZone,
      appointmentId,
      coachId,
      user?.address?.state,
      switchingCoach,
      appointmentSchedulerMonthlyDateFetchingEnabled,
    ],
  )

  useEffect(() => {
    if (!appointmentId || !appointmentSchedulerMonthlyDateFetchingEnabled) {
      if (appointmentSchedulerMonthlyDateFetchingEnabled && initialDate) {
        reload({
          startDate: moment(initialDate).startOf('month').toISOString(),
          endDate: moment(initialDate).endOf('month').toISOString(),
          month: moment(initialDate).format(MONTH_FORMAT),
        })
        return
      }

      reload({ month: moment().format(MONTH_FORMAT) })
      return
    }

    if (loadingAppointmentToReschedule || !appointmentToReschedule) {
      return
    }

    const rescheduleAppointmentCurrentDate = appointmentToReschedule.meetingAt
    if (rescheduleAppointmentCurrentDate) {
      reload({
        startDate: moment(rescheduleAppointmentCurrentDate).startOf('month').toISOString(),
        endDate: moment(rescheduleAppointmentCurrentDate).endOf('month').toISOString(),
        month: moment(rescheduleAppointmentCurrentDate).format(MONTH_FORMAT),
      })

      return
    }

    reload({ month: moment().format(MONTH_FORMAT) })
  }, [
    appointmentId,
    reload,
    loadingAppointmentToReschedule,
    appointmentToReschedule,
    appointmentSchedulerMonthlyDateFetchingEnabled,
    initialDate,
  ])

  const onMonthChange = (date: DateData) => {
    if (!appointmentSchedulerMonthlyDateFetchingEnabled) {
      return
    }

    const month = moment(date.dateString).format(MONTH_FORMAT)

    if (month === selectedMonth) {
      return
    }

    setSelectedMonth(month)

    const monthStart = moment(date.dateString).startOf('month')
    const monthEnd = moment(date.dateString).endOf('month')

    // Check if we've already fetched this month
    if (fetchedMonths.has(month)) {
      const monthDates = allAvailableDates.filter((availableDate) =>
        moment(availableDate).isBetween(monthStart, monthEnd, 'day', '[]'),
      )
      setCurrentAvailableDates(monthDates)
      setSelectedDate(monthDates.length > 0 ? monthDates[0] : null)
      return
    }

    setCurrentAvailableDates([])
    setSelectedDate(null)

    // Fetch dates for the new month and append to cache
    reload({
      startDate: monthStart.toISOString(),
      endDate: monthEnd.toISOString(),
      appendDates: true,
      month,
    })
  }

  return {
    availableDates: currentAvailableDates,
    crossKindRecurrenceDates,
    hasFetchedAnyMonth: fetchedMonths.size > 0,
    loading,
    selectedDate,
    setSelectedDate,
    selectedMonth,
    onMonthChange,
    error,
    reload,
  }
}

export const useEhrAvailableSlots = (
  appointmentKind: EhrAppointmentKind,
  cadence: EhrAppointmentRecurrenceCadence,
  date: string | null,
  coachId?: string,
) => {
  const user = useSelector(userSelector)
  const [availableSlots, setAvailableSlots] = useState<EhrSlot[]>([])
  const dispatch = useDispatch()
  const loading =
    useSelector(loadingEffectsSelector)['nutritionistHub/fetchEhrAvailableSlots'] || false

  const [prevDate, setPrevDate] = useState<string | null>(date)

  if (prevDate !== date) {
    setPrevDate(date)
    setAvailableSlots([])
  }

  useEffect(() => {
    if (!date) {
      return
    }
    dispatch({
      type: 'nutritionistHub/fetchEhrAvailableSlots',
      payload: {
        appointmentKind,
        timeZone: user?.timeZone,
        providerKind: EhrAppointmentProviderKind.Coach,
        date,
        providerId: coachId,
        cadence,
      },
      success: (response: Slots) => {
        setAvailableSlots(response.slots)
      },
    })
  }, [dispatch, date, appointmentKind, user?.timeZone, coachId, cadence])

  return {
    availableSlots,
    loading,
  }
}

export const useNutritionistHubTracker = () => {
  const insurancePolicy = useSelector(insurancePolicySelector)
  const latestEhrAppointment = useSelector(latestUserEhrAppointmentSelector)

  const track = useCallback(
    (event: CustomEventTypes, additionalProps?: MixpanelEventProperties) => {
      const defaultProps = {
        insurancePolicyState: insurancePolicy?.lastState?.kind,
        latestAppointment: latestEhrAppointment && {
          lastState: latestEhrAppointment.lastState.kind,
          appointmentKind: latestEhrAppointment.kind,
          date: latestEhrAppointment.meetingAt,
        },
      }
      Analytics.track(event, { ...defaultProps, ...additionalProps })
    },
    [insurancePolicy, latestEhrAppointment],
  )

  return track
}

export const useNoMoreAllotedCallsAlert = () => {
  const insurancePolicy = useSelector(insurancePolicySelector)
  const [showNoAllotedCallsAlert, setShowNoAllotedCallsAlert] = useState(false)
  const noAllotedCallsAlertClosedKey = `${Storage.NO_ALLOTTED_CALLS_ALERT_CLOSED_KEY}_${insurancePolicy?.id}`

  const onClose = () => {
    if (insurancePolicy) {
      Storage.set(noAllotedCallsAlertClosedKey, true)
    }
    setShowNoAllotedCallsAlert(false)
  }

  useFocusEffect(
    useCallback(() => {
      if (!insurancePolicy?.eligible) {
        return
      }

      if (!isUnusedCallsInfoAvailable(insurancePolicy) || insurancePolicy.hasUnusedCalls) {
        return
      }

      const isAllotedCallsAlertClosed = Storage.get<boolean>(noAllotedCallsAlertClosedKey)
      if (!isAllotedCallsAlertClosed) {
        setShowNoAllotedCallsAlert(true)
      }
    }, [setShowNoAllotedCallsAlert, insurancePolicy, noAllotedCallsAlertClosedKey]),
  )

  return { showNoAllotedCallsAlert, onClose }
}

export { useAddInsuranceNavigation } from './hooks/useAddInsuranceNavigation'
